
import { useState, useCallback, useEffect } from 'react';
import { Doctor } from '@/types';
import { doctorService } from '@/services/doctorService';
import { dbConnectionService } from '@/services/dbConnectionService';
import { toast } from '@/components/ui/sonner';

export const useDoctorFetching = () => {
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const [loadAttempts, setLoadAttempts] = useState(0);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  
  // Fetch doctors function, extracted so we can reuse it
  const loadDoctors = useCallback(async (forceRefresh = false) => {
    if (isLoading && !forceRefresh) return; // Prevent multiple simultaneous loads
    
    try {
      setIsLoading(true);
      
      // Set a timeout to ensure loading state doesn't persist indefinitely
      const timeoutId = setTimeout(() => {
        console.log("Loading doctors timeout reached");
        if (isLoading) {
          setConnectionError(true);
          setIsLoading(false);
        }
      }, 5000); // 5 seconds timeout
      
      // First try direct Supabase connection which is faster
      try {
        const data = await doctorService.getAll(forceRefresh);
        console.log("Loaded doctors:", data);
        setDoctors(data);
        setConnectionError(false);
        clearTimeout(timeoutId);
        setIsLoading(false);
        setInitialLoadDone(true);
        return;
      } catch (supabaseError) {
        console.error('Supabase error:', supabaseError);
        // Fall through to next approach
      }
      
      // If that fails, check if we should use mock data
      const shouldUseMock = await dbConnectionService.shouldUseMockData();
      
      if (shouldUseMock) {
        const data = await doctorService.getAll(forceRefresh);
        setDoctors(data);
        setConnectionError(false);
      } else {
        setConnectionError(true);
        if (!initialLoadDone) {
          setDoctors([]);
        }
      }
      
      clearTimeout(timeoutId);
    } catch (error: any) {
      console.error('Error loading doctors:', error);
      setConnectionError(true);
      
      // Don't show toast for initial load errors as ConnectionStatus will handle it
      if (initialLoadDone) {
        toast("Failed to refresh doctors. Please check your database connection.");
      }
    } finally {
      setIsLoading(false);
      setLoadAttempts(prev => prev + 1);
    }
  }, [initialLoadDone, isLoading]);
  
  // Initial load on component mount
  useEffect(() => {
    loadDoctors();
  }, []);
  
  // Add a second effect that retries loading after a delay if no doctors were loaded on first attempt
  useEffect(() => {
    // If we've already attempted to load once, doctors array is empty, and we're not in an error state
    if (loadAttempts === 1 && doctors.length === 0 && !connectionError && !isLoading) {
      console.log("No doctors loaded on first attempt, trying again in 2 seconds...");
      const retryTimer = setTimeout(() => {
        loadDoctors(true);
      }, 2000);
      
      return () => clearTimeout(retryTimer);
    }
    
    // If still no doctors after second attempt, try again after a longer delay
    if (loadAttempts === 2 && doctors.length === 0 && !isLoading) {
      console.log("No doctors loaded on second attempt, trying again in 3 seconds...");
      const retryTimer = setTimeout(() => {
        loadDoctors(true);
      }, 3000);
      
      return () => clearTimeout(retryTimer);
    }
  }, [loadAttempts, doctors.length, connectionError, isLoading, loadDoctors]);

  return {
    doctors,
    setDoctors,
    isLoading,
    setIsLoading,
    connectionError,
    setConnectionError,
    loadDoctors
  };
};
